import { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import styles from "./header.module.css"

export function Header() {
  const navigate = useNavigate()
  const { user, isAuthenticated, isLoading, logout } = useAuthStore()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const nickname = user?.user_metadata?.nickname || user?.email?.split("@")[0] || "用户"
  const avatarLetter = nickname[0]?.toUpperCase() || "U"
  const displayEmail = user?.email || ""

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // 键盘导航和 ESC 关闭
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [showDropdown])

  const handleLoginClick = () => {
    navigate("/login")
  }

  const handleUserClick = () => {
    setShowDropdown(!showDropdown)
  }

  const handleLogout = async () => {
    setShowDropdown(false)
    await logout()
    navigate("/")
  }

  const handleProfileClick = () => {
    setShowDropdown(false)
    navigate("/me")
  }

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        {/* Logo */}
        <Link to="/" className={styles.logo}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <span className={styles.logoText}>蓝血精英</span>
        </Link>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {isLoading ? (
            <div className={styles.loadingAvatar} />
          ) : isAuthenticated ? (
            <div className={styles.userMenu} ref={dropdownRef}>
              <button
                className={styles.userAvatar}
                onClick={handleUserClick}
                aria-expanded={showDropdown}
                aria-haspopup="menu"
              >
                <span className={styles.avatarLetter}>{avatarLetter}</span>
                <span className={styles.userEmail}>{displayEmail}</span>
                <svg
                  className={`${styles.chevronIcon} ${showDropdown ? styles.chevronUp : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {showDropdown && (
                <div className={styles.dropdown} role="menu">
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownAvatar}>{avatarLetter}</span>
                    <div className={styles.dropdownInfo}>
                      <span className={styles.dropdownNickname}>{nickname}</span>
                      <span className={styles.dropdownEmail}>{displayEmail}</span>
                    </div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <Link to="/me" className={styles.dropdownItem} onClick={handleProfileClick} role="menuitem">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span>个人中心</span>
                  </Link>
                  <button className={styles.dropdownItem} onClick={handleLogout} role="menuitem">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    <span>退出登录</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className={styles.loginBtn} onClick={handleLoginClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>登录</span>
            </button>
          )}
        </div>
      </div>
    </header>
  )
}